import React from "react"
import { joinClassNames } from "@common/lib/util"
import ActionMenuButton from "@common/table/buttons/actionMenuButton"
import { getCommonCellStyles } from "@common/table/column.util"
import { RTable } from "@common/table/types"
import { flexRender, Cell, Row } from "@tanstack/react-table"
import { useTheme } from "next-themes"
import Link from "next/link"

type BodyCellProps = {
  cell: Cell<any, any>
  table: RTable
  row: Row<any>
  colIndex: number
  rowLink?: string
  expandedRowId?: string
}

const BodyCell = ({
  cell,
  table,
  row,
  colIndex,
  rowLink,
  expandedRowId,
}: BodyCellProps) => {
  const { column } = cell
  const { theme } = useTheme()
  const computedStyles = getCommonCellStyles({ column, table, theme }) as any
  const {
    options: {
      rowActions,
      themes: { tdClasses, tdSpacingClasses, tdSelectedBgClasses, tdBgClasses },
    },
  } = table

  const actions = rowActions?.actions ?? row.original.rowActions
  const columnDef = column.columnDef as any
  const isDisplayColumn = columnDef.type === "display"
  return (
    <td
      key={cell.id}
      className={joinClassNames(
        tdClasses,
        column.id === "row-actions" || isDisplayColumn ? "" : tdSpacingClasses,
        row.getIsSelected() || expandedRowId === row.id
          ? tdSelectedBgClasses
          : tdBgClasses,
        isDisplayColumn ? "justify-center" : ""
      )}
      style={computedStyles}
      /* Prevent row click on checkbox cell */
      onClick={(e) => column.id === "row-select" && e.stopPropagation()}
    >
      {column.id === "row-actions" ? (
        <ActionMenuButton
          key={cell.id}
          row={row}
          actions={actions}
          onRowActionClick={rowActions?.onRowActionClick}
        />
      ) : /* Don't wrap cell with a div if it's row selection checkbox */ column.id ===
          "row-select" && column.columnDef.cell instanceof Function ? (
        column.columnDef.cell?.(cell.getContext())
      ) : cell.getIsPlaceholder() ? null : (
        <div
          style={{ paddingLeft: colIndex < 2 ? `${row.depth * 1}rem` : undefined }}
          className={isDisplayColumn ? "" : "w-full truncate"}
        >
          {rowLink ? (
            <Link href={rowLink} className="block h-full">
              {renderCell()}{" "}
            </Link>
          ) : (
            renderCell()
          )}
        </div>
      )}
    </td>
  )

  function renderCell() {
    return flexRender(column.columnDef.cell, cell.getContext())
  }
}

export default BodyCell
